import { useRef, useState, useCallback, useEffect } from 'react'
import type { KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Pencil, ArrowUp, Sparkles } from 'lucide-react'
import type { AppDispatch, RootState } from '@/redux'
import { selectIsPostPanelPhase } from './hooks/creationPhaseSelectors'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/custom/Loading/Loading'
import { SourcePickerBlock } from './components/creation/SourcePickerBlock'
import { CredentialPickerHarvestBlock } from './components/creation/CredentialPickerHarvestBlock'
import { CredentialPickerExistingBlock } from './components/creation/CredentialPickerExistingBlock'
import { DomainPickerBlock } from './components/creation/DomainPickerBlock'
import { FiltersBlock } from './components/creation/FiltersBlock'
import { PanelsProposedBlock } from './components/creation/PanelsProposedBlock'
import { RecreateApiKeyInstructionalPanel } from './components/creation/RecreateApiKeyInstructionalPanel'
import type { FlowStateName } from '@/types/DashboardV1.types'
import { selectAvailableCredentials } from '@/redux/slice/dashboardCreationSlice'
import { renameDashboardCreationThunk } from '@/services/dashboardsService'
import { sendChatMessage } from '@/services/DashboardChatService'
import { useDashboardCreationBootstrap } from './hooks/useDashboardCreationBootstrap'
import { BackButton } from '@/components/custom/BackButton'

// ─── Instructional panel context ──────────────────────────────────────────────
//
// Callbacks passed from DashboardCreationShell → resolveBlock → CredentialPickerGateway
// → CredentialPickerExistingBlock so the access_denied blocked row can request the
// side panel without the panel being rendered inside the chat bubble.
// The panel renders as a flex sibling to the main creation shell column.

interface InstructionalPanelCallbacks {
  onOpenInstructionalPanel: (credentialId: string) => void
  instructionalPanelOpenForCredentialId: string | null
  onCloseInstructionalPanel: () => void
}

// ─── Step derivation from FlowState ───────────────────────────────────────────
//
// Gate           → Step  Dot behaviour
// awaiting_source  → 1    dot 1 filled
// awaiting_credential → 2  dots 1-2 filled
// awaiting_domain → 3    dots 1-3 filled
// awaiting_filters → 4   dots 1-4 filled
// awaiting_panels → 5    all 5 filled
// complete        → 5    all filled (indicator hidden by navigation)

interface StepConfig {
  step: number
  label: string
}

const STEP_MAP: Record<FlowStateName, StepConfig> = {
  awaiting_source: { step: 1, label: 'Step 1 of 5' },
  awaiting_credential: { step: 2, label: 'Step 2 of 5' },
  awaiting_domain: { step: 3, label: 'Step 3 of 5' },
  awaiting_filters: { step: 4, label: 'Step 4 of 5' },
  awaiting_panels: { step: 5, label: 'Step 5 of 5' },
  complete: { step: 5, label: 'Step 5 of 5' },
}

// ─── Progress dot rendering ────────────────────────────────────────────────────

function ProgressDot({
  dotIndex,
  currentStep,
}: {
  dotIndex: number
  currentStep: number
}) {
  // dotIndex is 1-based (1..5)
  const filled = dotIndex < currentStep

  const baseClass = 'w-6 h-1.5 rounded-full'

  if (filled || dotIndex === currentStep) {
    return (
      <div
        className={`${baseClass} bg-accent`}
        data-testid="progress-dot"
        data-fill="full"
        aria-hidden="true"
      />
    )
  }

  return (
    <div
      className={`${baseClass} bg-border`}
      data-testid="progress-dot"
      data-fill="empty"
      aria-hidden="true"
    />
  )
}

// ─── Inline-editable dashboard name ───────────────────────────────────────────

interface EditableNameProps {
  name: string
  /**
   * Called with (newName, previousName) so the parent can pass the prior name
   * into the PATCH thunk for optimistic-update rollback if the request fails.
   */
  onRename: (value: string, previousName: string) => void
}

function EditableDashboardName({ name, onRename }: EditableNameProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  // sync draft when external name changes (AI rename or rollback)
  useEffect(() => {
    if (!editing) {
      setDraft(name)
    }
  }, [name, editing])

  const handleCommit = () => {
    setEditing(false)
    const trimmed = draft.trim()
    const committed = trimmed !== '' ? trimmed : 'Untitled Dashboard'
    onRename(committed, name)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCommit()
    } else if (e.key === 'Escape') {
      setDraft(name)
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
        className="text-[17px] font-bold text-primary bg-muted px-1.5 py-0.5 rounded-md border border-accent focus:outline-none focus:ring-2 focus:ring-focusRing focus:ring-offset-2 min-w-0 max-w-[240px]"
        aria-label="Edit dashboard name"
        autoFocus
      />
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Dashboard name: ${name} — click to rename`}
      className="flex items-center gap-1.5 cursor-text group"
      onClick={() => setEditing(true)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          setEditing(true)
        } else if (e.key === ' ') {
          e.preventDefault()
          setEditing(true)
        }
      }}
    >
      <span className="text-[17px] font-bold text-primary px-1.5 py-0.5 rounded-md group-hover:bg-muted transition-colors">
        {name}
      </span>
      <Pencil
        size={14}
        className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
        aria-hidden="true"
      />
    </div>
  )
}

// ─── Block router ─────────────────────────────────────────────────────────────

/**
 * resolveBlock — returns the JSX element to render inside the AI bubble for
 * the given flowState, or `null` when no block should render.
 *
 * Plain function (not a React component) so callers can inspect the return
 * value to drive the null-content guard in `ChatMessageRegion`. Encoding the
 * "no content" decision once here means future flow states that also render
 * nothing (e.g. an explicit `idle` gate) just return `null` and the guard
 * stays correct without code changes.
 *
 * `awaiting_credential` delegates to `CredentialPickerGateway`, which reads
 * `availableCredentials` from Redux and routes to mockup 04 or 05 (TDD §3d.1.E).
 *
 * `panelCallbacks` is passed through to `CredentialPickerGateway` so the
 * access_denied blocked row can request the instructional side panel without
 * the panel being rendered inside the chat bubble. Phase E Slice 2.4.
 */
function resolveBlock(
  flowState: FlowStateName,
  panelCallbacks: InstructionalPanelCallbacks,
): JSX.Element | null {
  switch (flowState) {
    case 'awaiting_source':
      return <SourcePickerBlock />
    case 'awaiting_credential':
      return <CredentialPickerGateway panelCallbacks={panelCallbacks} />
    case 'awaiting_domain':
      return <DomainPickerBlock />
    case 'awaiting_filters':
      return <FiltersBlock />
    case 'awaiting_panels':
      return <PanelsProposedBlock />
    case 'complete':
      return null
    default:
      return <SourcePickerBlock />
  }
}

/**
 * CredentialPickerGateway — reads availableCredentials from Redux and renders
 * the correct picker variant per TDD §3d.1.E.
 *
 * Decision key (frontend-owned):
 *   availableCredentials.length === 0  → <CredentialPickerHarvestBlock /> (mockup 04)
 *   availableCredentials.length >= 1   → <CredentialPickerExistingBlock /> (mockup 05)
 *   isAddingNew === true               → <CredentialPickerHarvestBlock /> (mockup 04)
 *
 * availableCredentials is populated by the `present_credential` tool-call handler
 * in DashboardChatService when the FSM enters awaiting_credential. An empty array
 * is a valid signal that the org has no existing credentials for the source.
 *
 * BUG-04 fix: when the user clicks "Add a new CampMinder connection" in the
 * existing-creds picker, `onAddNew` fires and sets `isAddingNew = true`, which
 * switches the gateway to render <CredentialPickerHarvestBlock />. This produces
 * the same harvest flow as if the org had no existing credentials.
 *
 * `panelCallbacks` is forwarded to CredentialPickerExistingBlock so the
 * access_denied blocked row can request the instructional side panel. Phase E Slice 2.4.
 */
function CredentialPickerGateway({ panelCallbacks }: { panelCallbacks: InstructionalPanelCallbacks }) {
  const availableCredentials = useSelector(selectAvailableCredentials)
  const sourceAppCode = useSelector((s: RootState) => s.dashboardCreation.sourceAppCode ?? 'campminder')
  const [isAddingNew, setIsAddingNew] = useState(false)

  const handleAddNew = useCallback(() => {
    setIsAddingNew(true)
  }, [])

  // Bug #1 follow-up 2026-05-14: the harvest block's "Use an existing credential"
  // escape hatch must be able to reset `isAddingNew` — otherwise clicking the
  // link from inside the add-new flow refires the prefetch but the gateway
  // stays on the harvest block (because isAddingNew is still true), and the
  // flash erroneously fires even when credentials exist.
  const handleCancelAddNew = useCallback(() => {
    setIsAddingNew(false)
  }, [])

  if (availableCredentials.length === 0 || isAddingNew) {
    return <CredentialPickerHarvestBlock onCancelAddNew={isAddingNew ? handleCancelAddNew : undefined} />
  }

  return (
    <CredentialPickerExistingBlock
      credentials={availableCredentials}
      sourceAppCode={sourceAppCode}
      onAddNew={handleAddNew}
      onOpenInstructionalPanel={panelCallbacks.onOpenInstructionalPanel}
      instructionalPanelOpenForCredentialId={panelCallbacks.instructionalPanelOpenForCredentialId}
    />
  )
}

// ─── Chat input bar ────────────────────────────────────────────────────────────

function ChatInputBar({ dashboardId, token }: { dashboardId: string; token: string }) {
  const [value, setValue] = useState('')
  const dispatch: AppDispatch = useDispatch()
  const isSending = useSelector((s: RootState) => s.chatSession.isSending)

  const handleSend = async () => {
    const text = value.trim()
    if (!text || isSending) return
    setValue('')
    await dispatch(sendChatMessage(token, dashboardId, text))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  return (
    <div className="border-t border-border bg-card flex justify-center flex-shrink-0 py-3.5">
      <div className="flex items-end gap-2.5 w-full max-w-[760px] px-20">
        <div className="flex-1 relative">
          <Textarea
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Select an option above, or ask me a question…"
            rows={1}
            aria-label="Message the AI assistant"
            className="resize-none min-h-[44px] max-h-[120px] text-sm border-border rounded-[10px] pr-12 py-3"
          />
          <Button
            size="icon"
            className="absolute right-2 bottom-2 w-8 h-8 rounded-lg bg-accent hover:bg-accent/90"
            aria-label="Send message"
            disabled={value.trim() === '' || isSending}
            onClick={() => void handleSend()}
          >
            <ArrowUp size={14} className="text-white" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── AI avatar ─────────────────────────────────────────────────────────────────

function AIAvatar() {
  return (
    <div
      className="w-[34px] h-[34px] rounded-full bg-gradient-to-br from-accent to-blue-700 flex items-center justify-center flex-shrink-0 absolute -left-12 top-0"
      style={{ boxShadow: '0 0 0 3px hsl(var(--accent-background))' }}
      aria-hidden="true"
    >
      <Sparkles size={16} className="text-white" aria-hidden="true" />
    </div>
  )
}

// ─── Chat message region (null-content guard, TDD §3d.1.E) ────────────────────

interface ChatMessageRegionProps {
  flowState: FlowStateName
  panelCallbacks: InstructionalPanelCallbacks
}

/**
 * ChatMessageRegion — the chat log container.
 *
 * Renders, in order:
 *   1. Every persisted chat message (`chatSession.messages`) — both
 *      `kind: 'template'` rows persisted by the deterministic FSM
 *      (Slice 1.3) AND `kind: 'llm'` rows from the AI chat endpoint.
 *      User and assistant messages render as right-aligned and left-aligned
 *      bubbles respectively per v3 mockups (04, 11, 12, 14).
 *   2. The current FSM block (filter card, credential picker, etc.) wrapped
 *      in an AI bubble so it visually anchors the *active* interactive
 *      surface as the latest assistant turn.
 *
 * Null-content guard (TDD §3d.1.E): the AI block-bubble renders ONLY when
 * `resolveBlock` returns a non-null element. Driving the guard from the
 * router's actual return value (rather than a hard-coded `flowState !==
 * 'complete'` check) means any future flow state that returns `null` from
 * `resolveBlock` will not produce a timestamp-only empty bubble.
 */
function ChatMessageRegion({ flowState, panelCallbacks }: ChatMessageRegionProps) {
  const block = resolveBlock(flowState, panelCallbacks)
  const messages = useSelector((state: RootState) => state.chatSession.messages)
  const messagesScrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to the bottom when new messages arrive or the active block
  // changes — keeps the latest turn in view as the user progresses.
  useEffect(() => {
    const el = messagesScrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, flowState])

  return (
    <div
      ref={messagesScrollRef}
      // min-h-0: same flexbox gotcha as the parent <section>. Without it, a
      // tall active block (filter card with many sessions, panel proposal
      // catalog, etc.) makes this container grow past its flex-1 allotment
      // and pushes the chat input below the viewport instead of scrolling
      // internally. overflow-y-auto on the outer container (full-width)
      // ensures scrollbar appears on the far right, while inner content is
      // constrained to max-w-[760px] and centered.
      className="flex-1 overflow-y-auto min-h-0"
      role="log"
      aria-live="polite"
      aria-atomic="false"
    >
      <div className="px-20 py-8 flex flex-col gap-5 max-w-[760px] w-full mx-auto">
        {messages.map((msg, idx) => {
          // Stable key: timestamp + role survives re-orderings (e.g. when
          // mergeMessagesByTimestamp interleaves template + LLM messages on
          // hard-refresh). Falls back to idx for the rare message that lacks a
          // timestamp. (PR #331 Claude review §4.)
          const key = `${msg.timestamp ?? 'no-ts'}-${msg.role}-${idx}`
          return msg.role === 'user' ? (
            <div key={key} className="flex justify-end">
              <div className="bg-accent text-white rounded-xl rounded-tr-[3px] px-4 py-3 text-sm max-w-[480px] whitespace-pre-wrap break-words">
                {msg.content}
              </div>
            </div>
          ) : (
            <div key={key} className="flex gap-3 items-start relative">
              <AIAvatar />
              <div className="bg-background rounded-xl rounded-tl-[3px] px-4 py-3.5 text-sm text-primary leading-relaxed max-w-[640px] w-full whitespace-pre-wrap">
                {msg.content}
              </div>
            </div>
          )
        })}
        {block !== null && (
          <div className="flex gap-3 items-start relative">
            <AIAvatar />
            <div className="bg-background rounded-xl rounded-tl-[3px] px-4 py-3.5 text-sm text-primary leading-relaxed max-w-[640px] w-full">
              {block}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── DashboardCreationShell ────────────────────────────────────────────────────

/**
 * DashboardCreationShell — the v1 5-gate creation shell.
 *
 * Architecture (TDD §3d.1.A, amended 2026-04-24):
 * - Calls useDashboardCreationBootstrap on mount to POST /v1/dashboards and
 *   initialise the chat session. Shows spinner/error until the record is created.
 * - Renders the header: back link + inline-editable name + 5-gate progress indicator
 * - Chat area renders the current block based on dashboardCreation.flowState (Redux)
 * - Chat input bar is always visible
 * - Block components are wrapped in an AI bubble
 *
 * This component is rendered directly at /dashboards/new (routes.tsx).
 * DashboardNew.tsx is retained as a backward-compatible re-export.
 */
export const DashboardCreationShell = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { dashboardName, flowState, sourceAppCode } = useSelector((state: RootState) => state.dashboardCreation)
  const { token } = useSelector((state: RootState) => state.user)
  const { sessionId, error: chatSessionError } = useSelector((state: RootState) => state.chatSession)
  const isPostPanelPhase = useSelector(selectIsPostPanelPhase)
  // Read availableCredentials here (before early returns) to determine the
  // credential label for the instructional panel without violating hooks rules.
  const availableCredentials = useSelector(selectAvailableCredentials)

  // Bootstrap: creates the dashboard record on mount (Strict Mode guarded) and
  // initialises the chat session. Returns state to render loading/error gates.
  const { dashboardId, isCreating, mountError, handleRetry, handleSessionRetry } =
    useDashboardCreationBootstrap()

  // ── Instructional panel state (access_denied recovery — §37g) ───────────────
  //
  // Managed at the shell level so the panel renders as a flex sibling to the
  // creation shell column, not inside the chat bubble.
  const [instructionalPanelCredentialId, setInstructionalPanelCredentialId] = useState<string | null>(null)
  // Success announcement for the access_denied recovery path — mirrors the sr-only
  // role="alert" that CredentialPickerExistingBlock fires for the auth_failed path.
  const [instructionalPanelSuccessAnnouncement, setInstructionalPanelSuccessAnnouncement] = useState<string>('')

  const handleOpenInstructionalPanel = useCallback((credentialId: string) => {
    setInstructionalPanelCredentialId(credentialId)
  }, [])

  const handleCloseInstructionalPanel = useCallback(() => {
    setInstructionalPanelCredentialId(null)
  }, [])

  // Guard: if the instructional panel's target credential disappears from the
  // availableCredentials list (e.g., full prefetch replaced the list), clear the
  // panel state so aria-expanded and the open-state button label stay in sync.
  useEffect(() => {
    if (
      instructionalPanelCredentialId &&
      !availableCredentials.some(c => c.id === instructionalPanelCredentialId)
    ) {
      setInstructionalPanelCredentialId(null)
    }
  }, [instructionalPanelCredentialId, availableCredentials])

  const panelCallbacks: InstructionalPanelCallbacks = {
    onOpenInstructionalPanel: handleOpenInstructionalPanel,
    instructionalPanelOpenForCredentialId: instructionalPanelCredentialId,
    onCloseInstructionalPanel: handleCloseInstructionalPanel,
  }

  const stepConfig = STEP_MAP[flowState] ?? STEP_MAP.awaiting_source

  // §3d.1.B — navigate-replace to /dashboards/:id the moment the post-panel phase begins.
  // This unmounts DashboardCreationShell and mounts DashboardCanvas, which owns all
  // post-panel rendering (loading state, canvas, chat rail). Do NOT render any canvas
  // inside this shell — DashboardCanvas is the canonical owner.
  useEffect(() => {
    if (isPostPanelPhase && dashboardId) {
      navigate(`/dashboards/${dashboardId}`, { replace: true })
    }
  }, [isPostPanelPhase, dashboardId, navigate])

  /**
   * Commit an inline name edit.
   *
   * Optimistically updates Redux and persists via PATCH /v1/dashboards/:id.
   * Passes `previousName` to the thunk so it can roll back on backend failure.
   *
   * Skips the backend call when `dashboardId` or `token` is not yet available
   * (e.g., during the brief mount window before the dashboard record is created).
   */
  const handleRename = (name: string, previousName: string) => {
    if (token && dashboardId) {
      void dispatch(renameDashboardCreationThunk(token, dashboardId, name, previousName))
    }
  }

  // ── Bootstrap loading / error gates ─────────────────────────────────────────

  // 1. Show spinner while POST /v1/dashboards is in flight
  if (isCreating) {
    return <Loading />
  }

  // 2. Error state if POST /v1/dashboards failed
  if (mountError) {
    return (
      <div className="ml-64 flex-1 flex flex-col h-screen overflow-hidden items-center justify-center">
        <div
          role="alert"
          className="bg-destructive-foreground border border-destructive rounded-lg px-6 py-5 text-sm text-destructive max-w-sm text-center"
        >
          <p className="font-semibold mb-3">{mountError}</p>
          <Button variant="destructive" onClick={handleRetry}>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  // 3. Error state if loadChatSession failed
  if (dashboardId && !sessionId && chatSessionError) {
    return (
      <div className="ml-64 flex-1 flex flex-col h-screen overflow-hidden items-center justify-center">
        <div
          role="alert"
          className="bg-destructive-foreground border border-destructive rounded-lg px-6 py-5 text-sm text-destructive max-w-sm text-center"
        >
          <p className="font-semibold mb-3">
            We couldn&apos;t connect to your AI session. Please try again.
          </p>
          <Button variant="destructive" onClick={handleSessionRetry}>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  // 4. Initial state before token is available (brief blank during cookie hydration)
  if (!dashboardId) {
    return <Loading />
  }

  // Determine the credentialLabel for the instructional panel from availableCredentials.
  // availableCredentials is read before early returns (hooks rules), used here.
  const instructionalPanelCredential = instructionalPanelCredentialId
    ? availableCredentials.find(c => c.id === instructionalPanelCredentialId)
    : null

  // Outer flex-row: creation column + instructional side panel (push layout — §37g).
  return (
    <div className="ml-64 flex-1 flex flex-row h-screen overflow-hidden">
      {/* ── Main creation column ─────────────────────────────────── */}
      <div
        className="flex-1 flex flex-col h-screen overflow-hidden bg-card min-w-0"
        data-testid="creation-shell"
      >
        {/* ── Header bar ─────────────────────────────────────────── */}
        <header className="flex items-center justify-between px-7 py-3.5 border-b border-border bg-card flex-shrink-0 gap-4">
          {/* Back link */}
          <BackButton buttonText="Dashboards" buttonLink="/dashboards" additionalClassName="!mb-0" />

          {/* Centre: inline-editable name */}
          <div className="flex items-center flex-1 justify-center">
            <EditableDashboardName name={dashboardName} onRename={handleRename} />
          </div>

          {/* Right: 5-gate progress indicator */}
          <div
            className="flex items-center gap-2.5 flex-shrink-0"
            role="progressbar"
            aria-valuenow={stepConfig.step}
            aria-valuemin={1}
            aria-valuemax={5}
            aria-label={`Dashboard creation: ${stepConfig.label}`}
          >
            <span className="text-xs text-secondary font-medium whitespace-nowrap">
              {stepConfig.label}
            </span>
            <div className="flex gap-1 items-center" aria-hidden="true">
              {[1, 2, 3, 4, 5].map(dotIndex => (
                <ProgressDot
                  key={dotIndex}
                  dotIndex={dotIndex}
                  currentStep={stepConfig.step}
                />
              ))}
            </div>
          </div>
        </header>

        {/* ── Chat area ──────────────────────────────────────────── */}
        {/* min-h-0: flex items default to min-height:auto, which lets tall
            children (e.g. a Sessions filter card with many checkboxes) push
            the container past flex-1's allotment — the chat input then drifts
            off the viewport bottom instead of staying pinned. min-h-0 lets
            flex-1 properly cap at remaining space so the inner ChatMessageRegion
            can scroll internally. Bug discovered 2026-04-29. */}
        <section
          className="flex-1 flex flex-col overflow-hidden min-h-0"
          aria-label="AI creation assistant"
        >
          {/* Messages */}
          <ChatMessageRegion flowState={flowState} panelCallbacks={panelCallbacks} />

          {/* Chat input */}
          <ChatInputBar dashboardId={dashboardId} token={token ?? ''} />
        </section>
      </div>

      {/* ── Instructional side panel (access_denied recovery — §37g) ── */}
      {/* Rendered as a flex sibling so it pushes (not overlays) the creation column. */}
      {/* Success announcement (sr-only) — mirrors auth_failed path in CredentialPickerExistingBlock. */}
      {instructionalPanelSuccessAnnouncement && (
        <p
          className="sr-only"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {instructionalPanelSuccessAnnouncement}
        </p>
      )}
      {instructionalPanelCredentialId && instructionalPanelCredential && (
        <RecreateApiKeyInstructionalPanel
          credentialId={instructionalPanelCredentialId}
          credentialLabel={instructionalPanelCredential.label}
          sourceAppCode={sourceAppCode ?? 'campminder'}
          onClose={handleCloseInstructionalPanel}
          onSuccess={(credentialLabel: string) => {
            handleCloseInstructionalPanel()
            setInstructionalPanelSuccessAnnouncement(`${credentialLabel} is now valid — you can select it.`)
          }}
        />
      )}
    </div>
  )
}
